import { supabase } from '../supabaseClient';

export const organizationsService = {

  // Superadmin: get all orgs with their subscription + effective limits
  async getAllWithLimits() {
    const { data, error } = await supabase
      .from('org_effective_limits')
      .select('*')
      .order('org_name');
    if (error) throw error;
    return data;
  },

  // Superadmin: get all raw orgs
  async getAll() {
    // Fetch orgs, subscriptions, and packages separately then merge
    const [{ data: orgs, error }, { data: subs }, { data: pkgs }] = await Promise.all([
      supabase.from('organizations').select('*').order('name'),
      supabase.from('subscriptions').select('*'),
      supabase.from('packages').select('*'),
    ]);
    if (error) throw error;

    return (orgs || []).map(org => {
      const sub = (subs || []).find(s => s.org_id === org.id);
      const pkg = sub ? (pkgs || []).find(p => p.id === sub.package_id) : null;
      return {
        ...org,
        subscriptions: sub ? [{ ...sub, packages: pkg }] : [],
      };
    });
  },

  async create(org) {
    const { data, error } = await supabase
      .from('organizations')
      .insert([{ ...org, slug: org.name.toLowerCase().replace(/\s+/g, '-'), updated_at: new Date().toISOString() }])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async update(id, changes) {
    const { data, error } = await supabase
      .from('organizations')
      .update({ ...changes, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async suspend(id) {
    return this.update(id, { status: 'suspended' });
  },

  async activate(id) {
    return this.update(id, { status: 'active' });
  },

  // ── Subscriptions ─────────────────────────────────────────────
  async createSubscription(orgId, packageId, billingCycle = 'monthly') {
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + 14);
    const { data, error } = await supabase
      .from('subscriptions')
      .insert([{
        org_id:        orgId,
        package_id:    packageId,
        status:        'trialing',
        billing_cycle: billingCycle,
        trial_ends_at: trialEnd.toISOString(),
        updated_at:    new Date().toISOString(),
      }])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateSubscription(orgId, changes) {
    const { data, error } = await supabase
      .from('subscriptions')
      .update({ ...changes, updated_at: new Date().toISOString() })
      .eq('org_id', orgId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Superadmin: change org's package (upsert — works even if no subscription exists yet)
  async changePackage(orgId, newPackageId) {
    const { data, error } = await supabase
      .from('subscriptions')
      .upsert({
        org_id:                  orgId,
        package_id:              newPackageId,
        status:                  'active',
        billing_cycle:           'monthly',
        updated_at:              new Date().toISOString(),
        asset_limit_override:    null,
        user_limit_override:     null,
        antenna_limit_override:  null,
        facility_limit_override: null,
        request_limit_override:  null,
        delivery_limit_override: null,
      }, { onConflict: 'org_id' })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Superadmin: set custom limit overrides for one org
  async setOverrides(orgId, overrides) {
    return this.updateSubscription(orgId, overrides);
  },

  // Superadmin: get all usage stats across all orgs
  async getAllUsage() {
    const month = new Date().toISOString().slice(0, 7);
    const { data, error } = await supabase
      .from('usage_counters')
      .select('*, organizations(name)')
      .eq('month', month);
    if (error) throw error;
    return data;
  },

  // Superadmin: get platform-wide counts
  async getPlatformStats() {
    const [orgs, assets, users, packages] = await Promise.all([
      supabase.from('organizations').select('id, status', { count: 'exact' }),
      supabase.from('assets').select('id', { count: 'exact' }),
      supabase.from('profiles').select('id', { count: 'exact' }),
      supabase.from('packages').select('id, name'),
    ]);
    return {
      totalOrgs:    orgs.count   || 0,
      activeOrgs:   (orgs.data||[]).filter(o => o.status === 'active').length,
      totalAssets:  assets.count || 0,
      totalUsers:   users.count  || 0,
      packages:     packages.data || [],
    };
  },
};
